import type { CSSProperties, PropsWithChildren } from 'react'
import type { DraggableSyntheticListeners, UniqueIdentifier } from '@dnd-kit/core'
import React, { createContext, useContext, useMemo } from 'react'
import styled from 'styled-components'

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface Props {
  id: UniqueIdentifier
  isDraggable: boolean
}

interface Context {
  attributes: Record<string, any>
  listeners: DraggableSyntheticListeners
  ref(node: HTMLElement | null): void
}

export const SortableItemContext = createContext<Context>({
  attributes: {},
  listeners: undefined,
  ref() {},
})

export default function SortableItem({
  children,
  id,
  isDraggable = false,
}: PropsWithChildren<Props>) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id })
  const context = useMemo(
    () => ({
      attributes,
      listeners,
      ref: setActivatorNodeRef,
    }),
    [attributes, listeners, setActivatorNodeRef]
  )
  const style: CSSProperties = {
    opacity: isDragging ? 0.4 : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
  }

  return (
    <SortableItemContext.Provider value={context}>
      <SortableItemStyled
        ref={setNodeRef}
        style={style}
        {...(isDraggable ? attributes : {})}
        {...(isDraggable ? listeners : {})}
      >
        {children}
      </SortableItemStyled>
    </SortableItemContext.Provider>
  )
}

export function DragHandle() {
  const { attributes, listeners, ref } = useContext(SortableItemContext)

  return (
    <DragHandleIcon {...attributes} {...listeners} ref={ref}>
      <i className="fas fa-grip-dots-vertical" />
    </DragHandleIcon>
  )
}

const SortableItemStyled = styled.li`
  display: flex;
  opacity: 1 !important;
`

const DragHandleIcon = styled.div`
  &:hover {
    opacity: 1;
  }

  i {
    flex: 0 0 auto;
    margin: auto;
    font-size: 12.8px;
    color: var(--ss-grey);
  }

  opacity: 0.8;
  display: flex;
  width: 16px;
  padding: 15px;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  touch-action: none;
  cursor: var(--cursor, pointer);
  border-radius: 5px;
  border: none;
  outline: none;
  appearance: none;
  background-color: transparent;
  -webkit-tap-highlight-color: transparent;
`



// WEBPACK FOOTER //
// ./src/components/SortableList/SortableItem/index.tsx